import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import CommonModal from "components/CommonComponents/CommonModal";
import { getTraineeCertificates } from "action/trainee";
import { useParams } from "react-router-dom";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import {
  ThumbnailPreview,
  mapBackendTemplateToFrontend,
  downloadImage,
  generateCertificateFile,
} from "components/CommonComponents/CertificatePreview";
const TraineeCertificates = () => {
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);
  const [preview, setPreview] = useState({
    isOpen: false,
    parsedTemplate: null,
  });
  const dispatch = useDispatch();

  const traineeCertificates = useSelector(
    (state) => state.trainees?.traineeCertificates,
  );

  useEffect(() => {
    if (!decodedId) return;

    const fetchTraineeCertificates = async () => {
      setLoading(true);
      await getTraineeCertificates(dispatch, decodedId);
      setLoading(false);
    };

    fetchTraineeCertificates();
  }, [dispatch, decodedId]);

  useEffect(() => {
    const certificateData = traineeCertificates?.data;

    if (certificateData?.certificates) {
      const formattedCertificates = certificateData.certificates.map((item) => {
        let certificateJson = {};

        try {
          certificateJson = item.certificateJson
            ? JSON.parse(item.certificateJson)
            : {};
        } catch (error) {
          certificateJson = {};
        }

        const fakeItem = {
          id: item.id,
          name: item.certificateNo,
          type: item.certificateType,
          bg_path: item.certificatePath || certificateJson?.PreviewImage || "",
          template: certificateJson,
        };

        const parsedTemplate = mapBackendTemplateToFrontend(fakeItem);

        return {
          ...item,
          certificateNo: item.certificateNo || "",
          certificateType:
            item.certificateType === "COMPLETION"
              ? "Certificate of Completion"
              : item.certificateType === "PARTICIPATION"
                ? "Certificate of Participation"
                : "Certificate",
          image: certificateJson?.PreviewImage || "",
          fileUrl: item.certificatePath || certificateJson?.PreviewImage || "",
          issueDate: item.createdAt || "",
          parsedTemplate,
        };
      });

      setCertificates(formattedCertificates);
    } else {
      setCertificates([]);
    }
  }, [traineeCertificates]);

  const handleViewCertificate = (rowData) => {
    setPreview({
      isOpen: true,
      parsedTemplate: rowData?.parsedTemplate || null,
    });
  };

  const handleDowmloadCertificate = async (rowData) => {
    const parsedTemplate = rowData.parsedTemplate;

    if (!parsedTemplate) {
      downloadImage(
        rowData?.fileUrl || rowData?.image || "",
        `${rowData?.certificateNo || "Certificate"}.jpg`
      );
      return;
    }

    const file = await generateCertificateFile(
      parsedTemplate,
      `${rowData.certificateNo || "Certificate"}.png`
    );
    if (file) {
      const fileUrl = window.URL.createObjectURL(file);
      await downloadImage(fileUrl, `${rowData.certificateNo || "Certificate"}.png`);
      window.URL.revokeObjectURL(fileUrl);
    }
  };

  const handlePreviewCancel = () => {
    setPreview({
      isOpen: false,
      parsedTemplate: null,
    });
  };

  return (
    <>
      <Card className="certificates-card mb-5">
        <CardBody className="p-4">
          {loading && (
            <div className="text-center py-5">
              <i className="fa fa-spinner fa-spin fa-2xl loader" />
            </div>
          )}
          
          {!loading && certificates.length === 0 && (
            <div className="loaderContainer-sm empty-style mt-4">
              <div className="empty-icon">
                <i className="fa-solid fa-circle-info"></i>
              </div>
              <span>No certificates found.</span>
            </div>
          )}

          {!loading && certificates.length > 0 && (
            <Row className="certificates-grid">
              {certificates.map((item) => (
                <Col key={item.id} xl="4" lg="6" md="6" className="mb-4">
                  <Card className="certificate-tile">
                    <div className="certificate-image-wrapper">
                      {item.parsedTemplate ? (
                        <div className="certificate-preview-frame">
                          <ThumbnailPreview
                            parsedTemplate={item.parsedTemplate}
                          />
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <i className="fas fa-certificate fa-3x text-muted" />
                        </div>
                      )}

                      {item.status === "EXPIRED" && (
                        <Badge className="certificate-status" color="danger">
                          Expired
                        </Badge>
                      )}
                    </div>
                     
                    <CardBody>
                      <Row className="align-items-start">
                        <Col xs="12" md="8">
                          <h4 className="certificate-title">
                            {item.certificateNo}
                          </h4>
                          <p className="certificate-course mb-1">
                            {item.courseName}
                          </p>
                          <span className="certificate-date">
                            Issued on {item.issueDate}
                          </span>
                        </Col>

                        <Col
                          xs="12"
                          md="4"
                          className="d-flex flex-row flex-lg-column mt-3 mt-md-0 justify-content-start justify-content-md-end"
                        >
                          <Button
                            size="sm"
                            outline
                            color="deep-blue"
                            className="m-lg-0 mb-lg-2 mr-2"
                            disabled={!item.parsedTemplate}
                            onClick={() => handleViewCertificate(item)}
                          >
                            View
                          </Button>

                          <Button
                            size="sm"
                            outline
                            color="vivid-red"
                            className="m-0"
                            disabled={!item.fileUrl}
                            onClick={() => handleDowmloadCertificate(item)}
                          >
                            Download
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <CommonModal
            isOpen={preview.isOpen}
            toggle={handlePreviewCancel}
            title="Certificate Preview"
            size="xl"
          >
            <div className="text-center p-0 pb-2">
              {preview.parsedTemplate && (
                <div className="certificate-preview-frame">
                  <ThumbnailPreview parsedTemplate={preview.parsedTemplate} />
                </div>
              )}
            </div>
          </CommonModal>
        </CardBody>
      </Card>
    </>
  );
};

export default TraineeCertificates;
